import type { McpServerConfig } from '../../api/mcp-servers';

export interface ChatToolSelection {
  taxKnowledgeEnabled: boolean;
  datevKnowledgeEnabled: boolean;
  mcpServerIds: number[];
}

export const EMPTY_CHAT_TOOL_SELECTION: ChatToolSelection = {
  taxKnowledgeEnabled: false,
  datevKnowledgeEnabled: false,
  mcpServerIds: [],
};

export function normalizeChatToolSelection(value: unknown): ChatToolSelection {
  const candidate =
    value && typeof value === 'object'
      ? (value as Partial<ChatToolSelection>)
      : {};
  const mcpServerIds = Array.isArray(candidate.mcpServerIds)
    ? Array.from(
        new Set(
          candidate.mcpServerIds.filter(
            (id): id is number =>
              typeof id === 'number' && Number.isInteger(id) && id > 0
          )
        )
      )
    : [];

  return {
    taxKnowledgeEnabled: candidate.taxKnowledgeEnabled === true,
    datevKnowledgeEnabled: candidate.datevKnowledgeEnabled === true,
    mcpServerIds,
  };
}

export function getAvailableMcpServers(
  servers: readonly McpServerConfig[]
): McpServerConfig[] {
  return servers.filter(
    (server) => server.enabled && server.connectionStatus === 'connected'
  );
}

export function getSelectedToolSourceCount(
  selection: ChatToolSelection,
  availableMcpServers: readonly McpServerConfig[]
): number {
  const availableMcpServerIds = new Set(
    availableMcpServers.map((server) => server.id)
  );
  return (
    Number(selection.taxKnowledgeEnabled) +
    Number(selection.datevKnowledgeEnabled) +
    selection.mcpServerIds.filter((id) => availableMcpServerIds.has(id)).length
  );
}

export async function persistChatToolSelection(
  conversationId: string,
  selection: ChatToolSelection,
  request: typeof fetch = fetch
): Promise<ChatToolSelection> {
  const response = await request(
    `/api/v1/chat/conversations/${conversationId}/tool-selection`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(selection),
    }
  );
  if (!response.ok) {
    throw new Error(
      (await response.text()).trim() || 'Failed to update tools.'
    );
  }
  return normalizeChatToolSelection(await response.json());
}
